import React, { useEffect, useMemo } from 'react';
import { ArrowsChevronRight } from '@vtbhome/juicyfront/indexIcon';
import { ROUTE_ADMIN_APPROVE_ORDERS } from '@shared/config/routes/routes';
import { Typography } from '@shared/ui/typography/typography';
import { COLORS } from '@shared/config/colors/colors';
import { CardSide } from '@shared/ui/card-side/card-side';
import { useOrdersListSharedQuery } from '@entities/order/libs/hooks/use-orders-list-shared-query/use-orders-list-shared-query';
import { getQueryError } from '@shared/lib/utils/get-query-error/get-query-error';
import { useCurrentUserSelector } from '@entities/current-user/model/selectors';
import dayjs from 'dayjs';
import { useCurrentOrganizationPreviewSharedQuery } from '@entities/organization/libs/hooks/use-current-organization-preview-shared-query/use-current-organization-preview-shared-query';
import { orderStatusTag } from '@entities/order/libs/utils/order-status-tag/order-status-tag';
import { NetworkStateNew } from '@shared/ui/network-state-new/network-state-new';
import { LinkScroll } from '@shared/ui/link-scroll/link-scroll';
import { SegmantTabs } from '@shared/ui/segment-tabs/segment-tabs';
import { useUrlTabs } from '@shared/lib/hooks/use-url-tabs/use-url-tabs';
import { OrganizationOrdersTabValues } from '../config/ui-constants';
import { useRequestsListSharedQuery } from '@entities/request/libs/hooks/use-request-list-shared-query/use-request-list-shared-query';
import { organizationOrdersTabs } from '../lib/organization-orders-tabs/organization-orders-tabs';
import { DEFAULT_ORDERS_SIDE_LIST_PARAMETERS, DEFAULT_REQUESTS_SIDE_LIST_PARAMETERS } from '../config/store-constants';
import { requestStatusTag } from '@entities/request/libs/hooks/utils/request-status-tag/request-status-tag';
import { Wrapper, Header, Content, List, Title } from './side-list-own-orders.styles';
import { SideListOwnOrdersProps } from './types';
import { usePrefetchOrder } from '@entities/order/api/order-api';

export const SideListOwnOrders: React.FC<SideListOwnOrdersProps> = ({ onClickOrderCard }) => {
    const user = useCurrentUserSelector();
    const companyId = user?.company?.id || undefined;
    const userId = user?.id || undefined;

    const [getOrders, getOrdersResult] = useOrdersListSharedQuery({
        ...DEFAULT_ORDERS_SIDE_LIST_PARAMETERS,
        companyIds: user?.company?.id ? [user?.company?.id] : [],
        userId,
    });
    const [getRequests, getRequestsResult] = useRequestsListSharedQuery({
        ...DEFAULT_REQUESTS_SIDE_LIST_PARAMETERS,
        companyId: user?.company?.id || 0,
        userId,
    });
    const [, currentOrganizationPreviewResult] = useCurrentOrganizationPreviewSharedQuery(companyId || 0);
    const manager = currentOrganizationPreviewResult.data?.manager;

    const error = getQueryError(getOrdersResult);

    const prefetchOrder = usePrefetchOrder('order');

    useEffect(() => {
        getOrders({
            ...DEFAULT_ORDERS_SIDE_LIST_PARAMETERS,
            companyIds: user?.company?.id ? [user?.company?.id] : [],
            userId,
        });
    }, [user]);

    useEffect(() => {
        getRequests({
            ...DEFAULT_REQUESTS_SIDE_LIST_PARAMETERS,
            companyId: user?.company?.id || 0,
            userId,
        });
    }, [user]);

    const tabs = useMemo(() => {
        return organizationOrdersTabs();
    }, []);

    const { tabValue, handleSetTabValue } = useUrlTabs({
        tabValues: Object.values(OrganizationOrdersTabValues),
        initialTabValue: OrganizationOrdersTabValues.Orders,
    });

    return (
        <Wrapper>
            <Header>
                <Title>
                    <Typography size='h6'>Сделки организации</Typography>
                </Title>
                <LinkScroll to={ROUTE_ADMIN_APPROVE_ORDERS}>
                    <ArrowsChevronRight color={COLORS.neutral_50} />
                </LinkScroll>
            </Header>
            <Content>
                <SegmantTabs activeTabValue={tabValue} list={tabs} onChange={handleSetTabValue} />
                <List>
                    {tabValue === OrganizationOrdersTabValues.Orders && (
                        <NetworkStateNew
                            size='s'
                            isLoading={getOrdersResult.isFetching || getOrdersResult.isLoading}
                            isInitialized={Boolean(getOrdersResult.data)}
                            isError={getOrdersResult.isError}
                            isFiltered={Boolean(getOrdersResult.data?.search)}
                            hasData={Boolean(getOrdersResult.data?.total)}
                            errorProps={{
                                title: 'Ошибка загрузки сделок',
                                description: error?.message,
                            }}
                            emptyProps={{
                                title: 'Сделки отсутствуют',
                            }}
                            emptyFilteredProps={{
                                title: 'Сделки не найдены',
                            }}>
                            {getOrdersResult.data?.items.map((order) => {
                                const tag = orderStatusTag({ order, manager });
                                return (
                                    <CardSide
                                        key={order.id}
                                        title={order.product.name}
                                        description={dayjs(order.createdAt).utc().format('DD.MM.YY')}
                                        tagChildren={tag?.text}
                                        tagDeclineMessage={tag?.declineMessage}
                                        tagVariant={tag?.variant}
                                        onClick={() => onClickOrderCard(order.id)}
                                        onMouseEnter={() => prefetchOrder(order.id)}
                                    />
                                );
                            })}
                        </NetworkStateNew>
                    )}
                    {tabValue === OrganizationOrdersTabValues.Requests && (
                        <NetworkStateNew
                            size='s'
                            isLoading={getRequestsResult.isFetching || getRequestsResult.isLoading}
                            isInitialized={Boolean(getRequestsResult.data)}
                            isError={getRequestsResult.isError}
                            isFiltered={Boolean(getRequestsResult.data?.search)}
                            hasData={Boolean(getRequestsResult.data?.total)}
                            errorProps={{
                                title: 'Ошибка загрузки запросов',
                                description: error?.message,
                            }}
                            emptyProps={{
                                title: 'Запросы отсутствуют',
                            }}
                            emptyFilteredProps={{
                                title: 'Запросы не найдены',
                            }}>
                            {getRequestsResult.data?.items.map((request) => {
                                const tag = requestStatusTag({ request, manager });
                                return (
                                    <CardSide
                                        key={request.id}
                                        title={request.product.name}
                                        description={dayjs(request.createdAt).utc().format('DD.MM.YY')}
                                        tagChildren={tag?.text}
                                        tagDeclineMessage={tag?.declineMessage}
                                        tagVariant={tag?.variant}
                                    />
                                );
                            })}
                        </NetworkStateNew>
                    )}
                </List>
            </Content>
        </Wrapper>
    );
};
